import { Fragment, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import Button from "../../../../common/Button";
import NewToolTip from "../../../../common/New-ToolTip";
import { ReactSVG } from "react-svg";
import {
  updateInterventionAssessment,
  updateInterventionStatus,
} from "../../../../../redux/modules/intervention/actions";
import { Menu, Transition } from "@headlessui/react";
import { InterventionStepsEnum } from "../../../text/data";
import { useStepNavigation } from "./useStepNavigation";
import CustomInput from "../../../../common/CustomInput";
import { formatDateToDD_MMM_YYYY } from "../../../../../helpers/functions";
import {
  ResultOptions,
  BillingPinOptions,
  PrescriberSuggestionOptions,
  InterventionStatus,
  InterventionFormTypes,
  getInterventionStatusLabel,
  isAnnualMedicationReviewIntervention,
} from "../data";
import { getInterventionLinkedMrServiceId } from "../../../../../helpers/medication-review-pmr-link";
import ConfirmationModalNew from "../../../../common/ConfirmationModalNew";
import InterventionForm from "../InterventionForm";

const Billing = ({ intervention, currentInterventionSteps, currentStep: activeStep, onNavigate }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { interventionId, id: patientId } = useParams();
  const { currentStep, nextStep, previousStep } = useStepNavigation(
    InterventionStepsEnum.Billing,
    currentInterventionSteps,
    activeStep
  );

  // View toggles and local state for Clinical Action card
  const [showClinicalAction, setShowClinicalAction] = useState(false);
  const formRoot =
    intervention?.interventionAssessment?.interventionAssessmentForm || {};
  const billing = formRoot?.billing || {};
  const documentation = formRoot?.documentation || {};

  // Check if medication update is complete
  const isMedicationUpdateComplete =
    documentation?.completeMedicationUpdate === true;
  const [medUpdateCompleteLocal, setMedUpdateCompleteLocal] = useState(isMedicationUpdateComplete);

  // Keep local flag in sync with store
  useEffect(() => {
    setMedUpdateCompleteLocal(isMedicationUpdateComplete);
  }, [isMedicationUpdateComplete]);

  // Check current intervention status
  const currentStatus = intervention?.status;
  const isWaitingProviderResponse =
    currentStatus === InterventionStatus.WaitingProviderResponse;

  const [result, setResult] = useState(billing?.Result || "Complete");
  const [billingPin, setBillingPin] = useState(billing?.BillingPin || null);
  const [billingDesc, setBillingDesc] = useState("");
  const [actionSaved, setActionSaved] = useState(null); // 'noUpdate' | 'updateRecord' | 'canceled' | null
  const [isEditing, setIsEditing] = useState(true);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openFormModal, setOpenFormModal] = useState(false);
  const [hasEdited, setHasEdited] = useState(false);
  const finalDocumentationSessionKey = interventionId
    ? `inv-doc-from-billing-final-${interventionId}`
    : null;
  const resultActionMap = {
    noUpdate: "NoUpdate",
    updateRecord: "UpdateMedicationRecord",
    canceled: "Canceled",
  };
  // Static form data for InterventionDocumentation
  const interventionDocumentationForm = {
    id: 1,
    name: "Intervention Documentation Form",
    enable: true,
    type: InterventionFormTypes.InterventionDocumentation,
    inActiveIcon: "/icons/intervention/forms/clinicalCommInActive.svg",
    activeIcon: "/icons/intervention/forms/clinicalCommActive.svg",
  };

  // Navigation function to open InterventionDocumentation form
  const handleViewInterventionForm = () => {
    // Open the documentation form in a centered modal overlay instead of navigating away
    setOpenFormModal(true);
  };
  // Handler for returned intervention form (same modal for now)
  const handleViewReturnedInterventionForm = () => {
    setOpenFormModal(true);
  };

  // Keep description in sync with selected billing pin
  const syncDescriptionWithPin = (pinValue) => {
    const match = BillingPinOptions.find((o) => o.value === pinValue);
    setBillingDesc(match ? match.description : "");
  };

  // Initialize description on mount from backend value
  useEffect(() => {
    if (billing?.BillingPin) {
      syncDescriptionWithPin(billing.BillingPin);
    }
  }, [billing?.BillingPin]);

  
  useEffect(() => {
    //reset default value as the intervention changes
    setHasEdited(false);
  }, [interventionId]);

  // Derived UI state from selected result
  const completedValue = useMemo(
    () =>
      ResultOptions.find((o) => o.label === "Complete")?.value || "Complete",
    []
  );
  const cancelledValue = useMemo(
    () =>
      ResultOptions.find((o) => o.label === "Cancelled / Deleted")?.value ||
      "CancelledDeleted",
    []
  );
  const isCompletedResult = result === completedValue;
  const isCancelledResult = result === cancelledValue;

  // Interventions raised inside a medication review - or raised from the chart
  // and linked back to one - keep the original two-button close-out, because
  // "Update & Save" is what routes the pharmacist through updating the
  // medication record. Chart-only interventions have no record to update, so
  // they get the single "Save & Complete".
  const isMedicationReviewIntervention =
    isAnnualMedicationReviewIntervention(intervention) ||
    getInterventionLinkedMrServiceId(intervention) != null;
  const disableActionButtons =
    isCompletedResult && (!billingPin || !billingDesc);

  // Load values from state when coming back to this screen
  useEffect(() => {
    if (!hasEdited) {
      const root =
        intervention?.interventionAssessment?.interventionAssessmentForm;
      const persisted = root?.billing;
      if (persisted) {
        const persistedResult = persisted.result ?? null;
        const persistedPin = persisted.billingPin ?? null;
        setResult(persistedResult);
        setBillingPin(persistedPin);
        if (persistedResult === cancelledValue) {
          setBillingDesc("");
        } else {
          syncDescriptionWithPin(persistedPin);
        }
        if (persisted.resultAction === "NoUpdate") setActionSaved("noUpdate");
        else if (persisted.resultAction === "UpdateMedicationRecord")
          setActionSaved("updateRecord");
        else if (persisted.resultAction === "Canceled")
          setActionSaved("canceled");
        else setActionSaved(null);
        // If billing exists, show the Clinical Action card only
        if (persistedResult || persistedPin || persisted.resultAction) {
          setShowClinicalAction(true);
          setIsEditing(false);
        }
      }
    }
  }, [intervention]);

  

  const goToDocumentation = async () => {
    try {
      if (finalDocumentationSessionKey && typeof sessionStorage !== "undefined") {
        sessionStorage.setItem(finalDocumentationSessionKey, "1");
      }

      // Persist first; only move to the previous tab once the save succeeds.
      const result = await onSaveAssessment(previousStep, actionSaved);
      if (result) {
        onNavigate?.(previousStep);
      }
    } catch (error) {
      console.error("Failed to update intervention step:", error);
    }
  };

  // Final review lives on its own Summary tab after Billing, so this moves
  // forward and leaves the Documentation step untouched.
  const goToSummary = async () => {
    try {
      // Persist first; only move to the Summary tab once the save succeeds.
      const saveResult = await onSaveAssessment(nextStep, actionSaved);
      if (saveResult) {
        onNavigate?.(nextStep);
      }
    } catch (error) {
      console.error("Failed to update intervention step:", error);
    }
  };

  const onSaveAssessment = async (targetStep, action) => {
    try {
      const resultAction = resultActionMap[action] || null;

      // Prepare billing data to persist
      const billingData = {
        BillingPin: billingPin || null,
        Result: result || null,
        ResultAction: resultAction,
      };

      const assessmentUpdate = {
        currentStep: targetStep,
        billing: billingData,
      };

      // Stamp the assessment end date once, when Billing first completes the
      // record. The assessment mapper only overwrites non-null fields, so
      // omitting it on later saves preserves the original completion date.
      const isCompletingAction =
        resultAction === "NoUpdate" || resultAction === "UpdateMedicationRecord";
      if (isCompletingAction && !formRoot?.endDateTime) {
        assessmentUpdate.endDateTime = new Date().toISOString();
      }

      // Continue path should always open Medication card in Documentation first
      if (action === "updateRecord") {
        assessmentUpdate.documentation = {
          completeMedicationUpdate: false,
          releasedToDocumentationForms: false,
        };
        setMedUpdateCompleteLocal(false);
      }

      // If the user edited billing values, reset documentation flag now
      if (hasEdited) {
        assessmentUpdate.documentation = { completeMedicationUpdate: false };
        setMedUpdateCompleteLocal(false);
      }

      const saveResult = await dispatch(
        updateInterventionAssessment(interventionId, assessmentUpdate)
      );
      setHasEdited(false);
      return saveResult;
    } catch (error) {
      console.error("Failed to update intervention step:", error);
      return null;
    }
  };

  const onChangeInterventionStatus = async (status, action) => {
    try {
      await onSaveAssessment(currentStep, action);

      await dispatch(
        updateInterventionStatus(patientId, interventionId, status)
      );
      setIsEditing(false);
      setMedUpdateCompleteLocal(false);
      setHasEdited(false);
    } catch (error) {
      console.error("Failed to update intervention Status:", error);
    }
  };

  const onPrevious = async () => {
    try {
      const assessmentUpdate = {
        currentStep: previousStep,
      };

      // Persist first; only move to the previous tab once the save succeeds.
      const result = await dispatch(
        updateInterventionAssessment(interventionId, assessmentUpdate)
      );
      if (result) {
        onNavigate?.(previousStep);
      }
    } catch (error) {
      console.error("Failed to update intervention step:", error);
    }
  };

  // Back behavior inside Billing: close Pending Clinical Action first, otherwise navigate step back.
  const handleBillingBack = async () => {
    if (showClinicalAction) {
      setShowClinicalAction(false);
      return;
    }
    await onPrevious();
  };

  // Determine if buttons should be enabled
  const canGoNext = false; // This is the last step
  const canGoPrevious = true; // Can go back to step 6
  if (!intervention) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="text-gray-400 text-xl font-medium mb-4">
            No Intervention Data
          </div>
          <div className="text-gray-500 text-base">
            Billing information is not available.
          </div>
        </div>
      </div>
    );
  }

  // Deprecated local billing read; values initialized above from formRoot.billing

  return (
    <div className="w-full max-h-fit overflow-auto">
        <div className="w-full h-full flex flex-col px-6 py-3 -ml-5">
          <div className="flex gap-8 flex-1">
            {/* In Review Card (visible until action required is clicked) */}
            {!showClinicalAction && (
              <div
                id="notificationOfService"
                className={`h-fit w-[550px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col`}
              >
                <div className="w-[550px] h-[72px] gap-[10px] opacity-1 px-6 py-3 border-t-[1px] border-t-gray-100 border-t-l-[4px] border-t-r-[4px] bg-gray-20 border border-gray-100 flex items-center justify-between">
                  <h3 className="text-teal-300 font-semibold text-xl">
                    Intervention In Review
                  </h3>
                </div>
                <div className="flex-1 p-6 gap-[16px] flex flex-col bg-gray-10">
                  {/* Waiting Provider Response row */}
                  <div
                    className={`flex items-center justify-between rounded px-4 py-3 border ${
                      intervention?.status ===
                      InterventionStatus.WaitingProviderResponse
                        ? "bg-yellow-50 border-yellow-200"
                        : "bg-gray-20 border-gray-100"
                    }`}
                  >
                    <div className="text-gray-700 text-sm font-medium">
                      {getInterventionStatusLabel(intervention?.status)}
                    </div>
                    <div className="text-gray-700 text-sm font-medium">
                      {formatDateToDD_MMM_YYYY(
                        new Date(
                          intervention?.interventionAssessment
                            ?.lastModifiedDate || new Date()
                        )
                      )}
                    </div>
                  </div>

                  <div className={`w-full h-[1px] bg-gray-100`} />
                  <div className="flex flex-col gap-4">
                    <p className="text-lg font-semibold text-gray-800">
                      Billing PIN
                    </p>
                  </div>
                  <div>
                    <CustomInput
                      name="billingpin"
                      type="text"
                      value={`Pending ${BillingPinOptions[0].value}`}
                      disabled={true}
                      className="!bg-white !opacity-100"
                    />
                  </div>

                  <div className="flex flex-col gap-4">
                    <p className="text-lg font-semibold text-gray-800">
                      Billing Description
                    </p>
                  </div>
                  <div>
                    <CustomInput
                      name="billingdesc"
                      type="textarea"
                      value={BillingPinOptions[0].description}
                      disabled={true}
                      className="!bg-white !opacity-100"
                    />
                  </div>
                  <div className={`w-full h-[1px] bg-gray-100`} />
                  {/* Suggestions for Prescriber (read-only copy from Prescriber Follow Up) */}
                  <div className="">
                    <div className="rounded border border-gray-100 bg-white p-3">
                      <div className="text-base font-semibold text-teal-300 mb-2">
                        Suggestions for Prescriber
                      </div>
                      {PrescriberSuggestionOptions.map((opt, idx) => {
                        const selectedFromBackend =
                          formRoot?.prescriberFollowUp?.prescriberSuggestions;
                        const isChecked = selectedFromBackend
                          ? opt.label === selectedFromBackend
                          : idx === 0; // default to first if no backend value
                        return (
                          <div
                            key={opt.label}
                            className="flex items-center gap-2"
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              readOnly
                              className="mt-1"
                            />
                            <label
                              className={`text-sm ${
                                isChecked
                                  ? "text-teal-300 font-semibold"
                                  : "text-gray-700"
                              }`}
                            >
                              {opt.label}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Clinical Action Card (visible after action required is clicked) */}
            {showClinicalAction && (
              <div
                className={`w-[620px] rounded-[4px] overflow-visible border border-gray-100 flex flex-col`}
              >
                <div className="w-[620px] h-[72px] gap-[10px] opacity-1 px-6 py-3 bg-gray-20 border border-gray-100 flex items-center justify-between">
                  <h3 className="text-teal-300 font-semibold text-lg">
                    Pending Clinical Action
                  </h3>
                  <div className="flex items-center gap-3">
                    {isEditing ? (
                      // Red tag while editing
                      <div className="inline-flex text-base items-center gap-[10px] rounded-[4px] border border-red-100 bg-red-50 px-3 py-2">
                        <span className="inline-block w-[14px] h-[14px] rounded-full bg-red-200" />
                        <span className="text-[18px] leading-[25px] font-medium text-gray-600">
                          Received - Action Required
                        </span>
                      </div>
                    ) : isCancelledResult ? (
                      // Gray tag when user selected Delete Intervention (cancelled result)
                      <div className="inline-flex text-base items-center gap-[10px] rounded-[4px] border border-gray-100 bg-gray-10 px-3 py-2">
                        <span className="inline-block w-[14px] h-[14px] rounded-full bg-gray-400" />
                        <span className="text-[18px] leading-[25px] font-medium text-gray-600">
                          Deleted
                        </span>
                      </div>
                    ) : actionSaved === "noUpdate" ||
                      actionSaved === "updateRecord" ? (
                      // Green tag when saved with either action
                      <div className="inline-flex text-base items-center min-w-[172px] gap-[10px] rounded-[4px] border border-green-200 bg-green-50 px-3 py-2">
                        <span className="inline-block w-[14px] h-[14px] rounded-full bg-green-200" />
                        <span className="text-[18px] leading-[25px] font-medium text-gray-600">
                          Resolved
                        </span>
                      </div>
                    ) : null}
                    {!isEditing && (
                      <Menu
                        as="div"
                        className="relative flex pt-1 pr-2 justify-end"
                      >
                        <div>
                          <Menu.Button>
                            <ReactSVG
                              src="/icons/triple-dot.svg"
                              className="flex w-2 scale-x-[1.5] scale-y-[1.5]"
                            />
                          </Menu.Button>
                        </div>
                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="transform translate-y-2 opacity-0 scale-95"
                          enterTo="transform translate-y-0 opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform translate-y-0 opacity-100 scale-100"
                          leaveTo="transform translate-y-2 opacity-0 scale-95"
                        >
                          <Menu.Items className="absolute left-6 top-6 z-50 w-24 origin-top-right bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-opacity-5">
                            <Menu.Item>
                              <button
                                className="block w-full text-left px-3 py-3 border-b border-b-gray-20 hover:bg-gray-100"
                                onClick={async () => {
                                  try {
                                    if (currentStatus === InterventionStatus.Cancelled) {
                                      await dispatch(
                                        updateInterventionStatus(
                                          patientId,
                                          interventionId,
                                          InterventionStatus.Active
                                        )
                                      );
                                    }

                                    if (result === cancelledValue) {
                                      setResult(completedValue);
                                    }

                                  setIsEditing(true);
                                  setActionSaved(null);
                                  } catch (error) {
                                    console.log("Failed to reopen intervention for editing:", error);
                                  }
                                }}
                              >
                                Edit
                              </button>
                            </Menu.Item>
                          </Menu.Items>
                        </Transition>
                      </Menu>
                    )}
                  </div>
                </div>
                <div className="flex-1 p-6 gap-[22px] flex flex-col bg-gray-10">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <p className="text-gray-800 text-base font-semibold">
                        Results
                      </p>
                      <div className="text-gray-700 text-sm font-medium">
                        {formatDateToDD_MMM_YYYY(new Date())}
                      </div>
                    </div>
                    <CustomInput
                      name="results"
                      type="dropdown"
                      options={ResultOptions}
                      value={result}
                      onChange={(e) => {
                        const val = e?.value ?? null;
                        setResult(val);
                        if (val === cancelledValue) {
                          // Clear dependent fields when cancelled/deleted is selected
                          setBillingPin(null);
                          setBillingDesc("");
                        }
                      }}
                      className="w-full !opacity-100 !bg-white"
                      disabled={!isEditing}
                    />
                  </div>

                  <div className={`w-full h-[1px] bg-gray-100`} />

                  <div className="flex flex-col gap-2">
                    <p className="text-gray-800 text-base font-semibold">
                      Billing PIN
                    </p>
                    <CustomInput
                      name="billingPin"
                      type="dropdown"
                      options={BillingPinOptions}
                      placeholder="Select Billing PIN"
                      value={billingPin}
                      onChange={(e) => {
                        const val = e?.value ?? null;
                        setBillingPin(val);
                        syncDescriptionWithPin(val);
                      }}
                      className={`w-full !opacity-100 ${
                        billingPin === null && isCompletedResult
                          ? "!bg-blue-100"
                          : "bg-white"
                      }`}
                      required={isCompletedResult}
                      disabled={isCancelledResult || !isEditing}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <p className="text-gray-800 text-base font-semibold">
                      Billing Description
                    </p>
                    <CustomInput
                      inputClassName={`${
                        billingPin === null && isCompletedResult
                          ? "!bg-blue-100"
                          : "bg-white"
                      }`}
                      name="billingDescription"
                      type="textarea"
                      value={billingDesc}
                      onChange={() => {}}
                      className={`w-full !h-full !opacity-100 ${
                        billingPin === null && isCompletedResult
                          ? "!bg-blue-100"
                          : "bg-white"
                      }`}
                      required={isCompletedResult}
                      disabled={true}
                    />
                  </div>

                  <div className="flex items-center justify-between text-gray-700 text-sm">
                    <p>
                      It is the pharmacist’s responsibility to ensure PIN is
                      accurate.
                    </p>
                    <a
                      href="#"
                      className="text-gray-900 underline underline-offset-2"
                      onClick={(e) => e.preventDefault()}
                    >
                      Reference
                    </a>
                  </div>

                  <div className="flex items-center gap-4 pt-2">
                    {(() => {
                      if (isCancelledResult) {
                        return (
                          <>
                            {isEditing ? (
                              <Button
                                type="primary"
                                text="Delete Intervention"
                                className="w-full max-h-[52px] !rounded-[6px] !px-3"
                                onClick={async () => {
                                  setOpenDeleteDialog(true);
                                  setMedUpdateCompleteLocal(false);
                                  setHasEdited(true);
                                }}
                              />
                            ) : null}
                          </>
                        );
                      }
                      return (
                        <>
                          {actionSaved === null && isEditing ? (
                            <>
                              {isMedicationReviewIntervention ? (
                                <>
                                  <Button
                                    type="primary-outline"
                                    text="Save & No Updates"
                                    className="w-[200px] max-h-[52px] !rounded-[6px] !px-3"
                                    onClick={async () => {
                                      // Update intervention status to Completed
                                      setActionSaved("noUpdate");
                                      await onChangeInterventionStatus(
                                        InterventionStatus.Completed, "noUpdate"
                                      );
                                    }}
                                    disabled={disableActionButtons}
                                  />
                                  <Button
                                    type="primary"
                                    text="Save & Update Medication Record"
                                    className="w-[340px] max-h-[52px] !rounded-[6px] !px-3"
                                    onClick={async () => {
                                      // Update intervention status to Completed
                                      setActionSaved("updateRecord");
                                      await onChangeInterventionStatus(
                                        InterventionStatus.Completed, "updateRecord"
                                      );
                                    }}
                                    disabled={disableActionButtons}
                                  />
                                </>
                              ) : (
                                <Button
                                  type="primary"
                                  text="Save & Complete"
                                  className="w-full max-h-[52px] !rounded-[6px] !px-3"
                                  onClick={async () => {
                                    // Update intervention status to Completed
                                    setActionSaved("noUpdate");
                                    await onChangeInterventionStatus(
                                      InterventionStatus.Completed, "noUpdate"
                                    );
                                  }}
                                  disabled={disableActionButtons}
                                />
                              )}
                            </>
                          ) : (
                            <div className="w-full flex flex-row justify-end items-center pr-2">
                              <span className="w-fit text-REELTeal font-bold text-base items-center flex">
                                Complete
                              </span>
                              <ReactSVG
                                src="/icons/medication-card-complete.svg"
                                className={`pl-4 w-fit flex items-center`}
                              />
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
            {/* Two-button Card: keep visible so it appears beside Pending Clinical Action */}
            {(
              <div
                className={`h-[595px] w-[550px] rounded-[4px] overflow-hidden border border-teal-200 flex items-center justify-center`}
              >
                <div className="flex flex-col items-center gap-4">
                  {!showClinicalAction && (
                    <Button
                      type="primary"
                      text="Received - Action Required"
                      className="w-[380px] max-h-[52px] !rounded-[6px]"
                      onClick={() => setShowClinicalAction(true)}
                    />
                  )}
                  <Button
                    type="primary-outline"
                    text="View Original Intervention Form"
                    className="w-[380px] max-h-[52px] !rounded-[6px]"
                    iconUrl={interventionDocumentationForm.inActiveIcon}
                    onClick={handleViewInterventionForm}
                  />
                  <div data-tooltip-id={`returned-form-coming-soon-${interventionId}`}>
                    <Button
                      type="primary-outline"
                      text="View Returned Intervention Form"
                      className="w-[380px] max-h-[52px] !rounded-[6px]"
                      iconUrl={interventionDocumentationForm.inActiveIcon}
                      onClick={handleViewReturnedInterventionForm}
                      disabled={true}
                    />
                  </div>
                  <NewToolTip
                    id={`returned-form-coming-soon-${interventionId}`}
                    offset={-6}
                    render={() => (
                      <div className="bg-gray-10 text-sm text-teal-300 px-3 py-1">
                        Form - Integration Coming soon
                      </div>
                    )}
                  />
                  {/* <button
                  type="button"
                  className="w-[320px] border rounded flex items-center justify-between px-6 bg-gray-50 border-gray-100 hover:bg-gray-100"
                  onClick={handleViewInterventionForm}
                >
                  <span className="text-sm py-4 text-gray-800">
                    {interventionDocumentationForm.name}
                  </span>
                  <button
                    type="button"
                    className="p-1 rounded cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewInterventionForm();
                    }}
                  >
                    <ReactSVG
                      src={interventionDocumentationForm.inActiveIcon}
                      className="w-6 h-6 flex flex-col justify-center"
                    />
                  </button>
                </button> */}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between space-x-4 p-4">
          <div className="w-full">{/* Progress indicator could go here */}</div>
          {!isCancelledResult && (
            <div className="flex items-center gap-3">
              <Button
                type="primary-outline"
                text="Back"
                className="w-[200px] max-h-[52px] !rounded-[30px]"
                onClick={handleBillingBack}
                disabled={!canGoPrevious}
              />
              {!isWaitingProviderResponse && (
                <>
                  {actionSaved === "noUpdate" ? (
                    <Button
                      type="primary"
                      text="View Final Documentation"
                      className="min-w-[200px] max-h-[52px] !rounded-[30px]"
                      onClick={goToSummary}
                      disabled={isEditing}
                    />
                  ) : (
                    <Button
                      type="primary"
                      text="Continue"
                      className="min-w-[200px] max-h-[52px] !rounded-[30px]"
                      onClick={goToDocumentation}
                      disabled={isEditing}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* delete notification dialog */}
        <ConfirmationModalNew
          open={openDeleteDialog}
          title="Confirm Delete Interventions"
          className="!min-w-[630px]"
          confirmText="Yes Proceed to Delete"
          cancelText="Cancel"
          content={() => (
            <div>
              <div className="flex font-semibold text-teal-300 text-xl mb-6">
                Are you sure you want to delete the Intervention?
              </div>
              <div className="flex font-regular text-gray-800 text-base mb-4">
                It will be moved to the Cancelled/Deleted list.
                <br />
                <br />
                Unsaved charting data will not be saved.
              </div>
            </div>
          )}
          onCross={() => setOpenDeleteDialog(false)}
          handleClose={() => setOpenDeleteDialog(false)}
          confirmAction={async () => {
            // Update intervention status to Cancelled
            setActionSaved("canceled");
            await onChangeInterventionStatus(InterventionStatus.Cancelled, "canceled");
            setOpenDeleteDialog(false);
          }}
        />
        {/* Intervention form modal (original form view) */}
        <ConfirmationModalNew
          open={openFormModal}
          handleClose={() => setOpenFormModal(false)}
          onCross={() => setOpenFormModal(false)}
          hideActions={true}
          setWideModal={true}
          className="!min-w-[1000px] !w-[1000px]"
          title={interventionDocumentationForm.name}
          content={() => (
            <InterventionForm
              intervention={intervention}
              formType={interventionDocumentationForm.type}
              hideIconButton={true}
              onBack={() => setOpenFormModal(false)}
            />
          )}
        />
      </div>
  );
};

export default Billing;
